/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { view } from "@risingstack/react-easy-state";
import { BsCameraVideoFill, BsMicFill, BsGlobe } from "react-icons/bs";
import { BiNetworkChart } from "react-icons/bi";
import {
  MdFace,
  MdCenterFocusStrong,
  MdLightMode,
  MdVideocamOff,
  MdVoiceOverOff,
  MdCheckCircle,
  MdCancel,
  MdOutlineHelpOutline,
  MdOutlineSettings,
} from "react-icons/md";
import { IoWarning } from "react-icons/io5";
import "./WelcomeScreen.css";
import ExamGuidelines from "./ExamGuidelines";
import SystemCheckDialog from "./SystemCheckDialog";

const WelcomeScreen = view(({ state, startPlayer, stopPlayer }) => {
  const [deviceStatus, setDeviceStatus] = useState({
    camera: { available: false, subtitle: "Checking camera..." },
    microphone: { available: false, subtitle: "Checking microphone..." },
    browser: { available: true, subtitle: "Chrome 128.0.0.0" },
    network: { available: true, subtitle: "Stable Connection" },
  });

  const [isChecking, setIsChecking] = useState(false);
  const [showSystemCheck, setShowSystemCheck] = useState(true);

  const checkMediaDevices = async () => {
    setIsChecking(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      // Clean up the stream
      stream.getTracks().forEach((track) => track.stop());

      // Update both camera and microphone status
      setDeviceStatus((prev) => ({
        ...prev,
        camera: {
          available: true,
          subtitle: "HD Camera Ready",
        },
        microphone: {
          available: true,
          subtitle: "Audio Input Ready",
        },
      }));

      return true;
    } catch (error) {
      console.error("Media devices check failed:", error);
      let errorMessage =
        "An error occurred while accessing your devices. Please check your hardware connections.";

      switch (error.name) {
        case "NotAllowedError":
          errorMessage =
            "Permission denied! Please allow access to camera and microphone in your browser settings.";
          break;
        case "NotFoundError":
          errorMessage =
            "No camera or microphone found! Please connect the required devices and try again.";
          break;
        case "NotReadableError":
          errorMessage =
            "Could not access your devices. They might be in use by another application.";
          break;
        default:
          errorMessage =
            "An error occurred while accessing your devices. Please check your hardware connections.";
          break;
      }

      setDeviceStatus((prev) => ({
        ...prev,
        camera: {
          available: false,
          subtitle: errorMessage,
        },
        microphone: {
          available: false,
          subtitle: errorMessage,
        },
      }));

      return false;
    } finally {
      setIsChecking(false);
    }
  };

  const checkBrowserCompatibility = () => {
    const isChrome =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const chromeVersion = parseInt(
      (/Chrome\/([0-9]+)/.exec(navigator.userAgent) || [0, 0])[1],
      10
    );

    setDeviceStatus((prev) => ({
      ...prev,
      browser: {
        available: isChrome && chromeVersion >= 80,
        subtitle: isChrome
          ? `Chrome ${chromeVersion}.0.0`
          : "Browser not supported",
      },
    }));
  };

  const checkNetworkSpeed = async () => {
    try {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      let networkStatus = "Stable Connection";

      if (connection) {
        networkStatus = `${connection.effectiveType.toUpperCase()} - ${Math.round(
          connection.downlink
        )} Mbps`;
      }

      setDeviceStatus((prev) => ({
        ...prev,
        network: {
          available: true,
          subtitle: networkStatus,
        },
      }));
    } catch (error) {
      console.error("Network check failed:", error);
      setDeviceStatus((prev) => ({
        ...prev,
        network: {
          available: true,
          subtitle: "Connection available",
        },
      }));
    }
  };

  const performDeviceCheck = async () => {
    checkBrowserCompatibility();
    await Promise.all([checkMediaDevices(), checkNetworkSpeed()]);
  };

  useEffect(() => {
    performDeviceCheck();
  }, []);

  const areAllRequirementsMet = () => {
    return (
      deviceStatus.camera.available &&
      deviceStatus.microphone.available &&
      deviceStatus.browser.available &&
      deviceStatus.network.available
    );
  };

  const closeSystemCheck = () => {
    if (areAllRequirementsMet()) {
      setShowSystemCheck(false);
    }
  };

  const openSystemCheck = () => {
    setShowSystemCheck(true);
  };

  const systemRequirements = [
    {
      icon: <BsCameraVideoFill />,
      title:
        "Camera: " +
        (deviceStatus.camera.available ? "Available" : "Unavailable"),
      subtitle: deviceStatus.camera.subtitle,
      status: deviceStatus.camera.available,
    },
    {
      icon: <BsMicFill />,
      title:
        "Microphone: " +
        (deviceStatus.microphone.available ? "Available" : "Unavailable"),
      subtitle: deviceStatus.microphone.subtitle,
      status: deviceStatus.microphone.available,
    },
    {
      icon: <BsGlobe />,
      title:
        "Browser: " +
        (deviceStatus.browser.available ? "Compatible" : "Incompatible"),
      subtitle: deviceStatus.browser.subtitle,
      status: deviceStatus.browser.available,
    },
    {
      icon: <BiNetworkChart />,
      title:
        "Network: " +
        (deviceStatus.network.available ? "Connected" : "Disconnected"),
      subtitle: deviceStatus.network.subtitle,
      status: deviceStatus.network.available,
    },
  ];

  const studentInstructions = [
    {
      icon: <BsCameraVideoFill />,
      text: "Ensure your camera and microphone are working properly",
    },
    {
      icon: <MdCenterFocusStrong />,
      text: "Stay within the camera frame throughout the exam",
    },
    {
      icon: <MdLightMode />,
      text: "Keep your face clearly visible and well-lit",
    },
    {
      icon: <MdFace />,
      text: "Avoid looking away from the screen frequently",
    },
    {
      icon: <MdVideocamOff />,
      text: "Do not use any virtual backgrounds",
    },
    {
      icon: <MdVoiceOverOff />,
      text: "Maintain a quiet environment",
    },
  ];

  return (
    <div className="welcome-screen">
      <div className="welcome-header">
        <h3>AcademicCore® Exam Suite</h3>
        <div className="header-actions">
          <button className="system-check-button" onClick={openSystemCheck}>
            <MdOutlineSettings /> System Check
          </button>
          <div className="session-id">ID: {state.channelName}</div>
        </div>
      </div>


      {/* Always show the Exam Guidelines */}
      <ExamGuidelines />

      {/* Show the system check dialog as a popup when needed */}
      {showSystemCheck && (
        <SystemCheckDialog
          deviceStatus={deviceStatus}
          performDeviceCheck={performDeviceCheck}
          isConnected={state.isMasterConnected}
          onClose={closeSystemCheck}
          allRequirementsMet={areAllRequirementsMet()}
        />
      )}

      <div className="welcome-footer">
        <button
          className={`start-button ${state.playerIsStarted ? "stop" : "start"}`}
          onClick={state.playerIsStarted ? stopPlayer : startPlayer}
          disabled={
            !state.isMasterConnected ||
            !deviceStatus.camera.available ||
            !deviceStatus.microphone.available
          }
        >
          {state.playerIsStarted ? "End Exam" : "Start Exam"}
        </button>
      </div>
    </div>
  );
});

export default WelcomeScreen;
